import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import useSound from 'use-sound';

import { EditorState } from 'draft-js';

import api from '../services/apiClient';

import { Message, MessageGroup, MessageGeneric } from '../interfaces/IMessage';
import DataClientService from '../interfaces/IDataClientService';
import DataChatUser from '../interfaces/IDataChatUser';

import notificationSound from '../assets/sound/notification-mensage.mp3';
import notificationSoundNewClient from '../assets/sound/notification-new-client.wav';

interface Messages {
  seqAtendimento: number;
  messages: Message[];
}

interface MessagesGroup {
  seqGrupoConversa: number;
  messages: MessageGroup[];
  qtdeMensagensNaoLidas: number;
}

interface UsersGroupChat {
  seqGrupoConversa: number;
  usuarios: string[];
}

interface MessageChatCache {
  seqChat: number;
  message: EditorState | null;
}

interface MessageContextData {
  messages: Messages[];
  messagesGroup: MessagesGroup[];
  clientsAccepted: DataClientService[];
  clientWaiting: DataClientService[];
  chatEmAndamento: DataClientService[];
  chatUser: DataChatUser[];
  usersGroupChat: UsersGroupChat[];
  loadClientsWaiting: () => void;
  loadChatEmAndamento: () => void;
  loadClientsAccepted: () => void;
  SetClientsAccepted: (clients: DataClientService[]) => void;
  SetChatUser: (chatUser: DataChatUser[]) => void;
  getMensages: (id: number, messages: Message[]) => void;
  getMensagesGroup: (
    seqGrupoConversa: number,
    messagesGroup: MessageGroup[],
    chatHistorico: string
  ) => void;
  removeMensage: (
    seqAtendimento: number,
    seqMensagem: number,
    mensagensAtendimento: Messages[],
    mensagensGrupo: MessagesGroup[],
    chatGroup: boolean
  ) => void;
  playSoundNotification: () => void;
  playSoundNotificationNewClient: () => void;
  loadDataClient: (id: number) => void;
  editMessage: MessageGeneric;
  setEditMessage: (message: MessageGeneric) => void;
  citarMessage: MessageGeneric;
  setCitarMessage: (message: MessageGeneric) => void;
  loadDataClienteRequestService: () => void;
  SetClientMessageService: (data: DataClientService[]) => void;
  clientMessageService: DataClientService[];
  addChatGroup: (data: DataChatUser) => void;
  getChatGroup: (seqConversa: number) => void;
  getChatGroupMsgPendente: () => void;
  updateMensageRead: (seqGrupoConversa: number) => void;
  updateMensageReadAtendimento: (seqAtendimento: number) => void;
  getUserGroupMsg: (idChat: number) => Promise<UsersGroupChat>;
  removeTagsHtml: (html: string) => string;
  messageChatCache: MessageChatCache[];
  setMessageChatCache: (
    updater: (prev: MessageChatCache[]) => MessageChatCache[]
  ) => void;
  alertNewClientIdTimeout: NodeJS.Timeout;
  setAlertNewClientIdTimeout: (intervalIDPlayChat: NodeJS.Timeout) => void;
}

const MessageContext = createContext<MessageContextData>(
  {} as MessageContextData
);

function useMessage(): MessageContextData {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessage must be used within an AuthProvider');
  }
  return context;
}

const MessageProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();

  const [messages, SetMessages] = useState<Messages[]>([] as Messages[]);
  const [messagesGroup, SetMessagesGroup] = useState<MessagesGroup[]>(
    [] as MessagesGroup[]
  );
  const [clientsAccepted, SetClientsAccepted] = useState<DataClientService[]>(
    [] as DataClientService[]
  );
  const [clientWaiting, SetClientWaiting] = useState<DataClientService[]>(
    [] as DataClientService[]
  );
  const [chatEmAndamento, SetChatEmAndamento] = useState<DataClientService[]>(
    [] as DataClientService[]
  );
  const [chatUser, SetChatUser] = useState<DataChatUser[]>([]);
  const [clientMessageService, SetClientMessageService] = useState<
    DataClientService[]
  >([] as DataClientService[]);
  const [editMessage, setEditMessage] = useState<MessageGeneric>(
    {} as MessageGeneric
  );
  const [citarMessage, setCitarMessage] = useState<MessageGeneric>(
    {} as MessageGeneric
  );
  const [playSoundNotification] = useSound(notificationSound, { volume: 1 });
  const [playSoundNotificationNewClient] = useSound(
    notificationSoundNewClient,
    { volume: 1 }
  );
  const [usersGroupChat, setUsersGroupChat] = useState<UsersGroupChat[]>(
    [] as UsersGroupChat[]
  );

  const [messageChatCache, setMessageChatCache] = useState<MessageChatCache[]>(
    [] as MessageChatCache[]
  );

  const [alertNewClientIdTimeout, setAlertNewClientIdTimeout] = useState(
    {} as NodeJS.Timer
  );

  const loadClientsAccepted = useCallback((): void => {
    const token = localStorage.getItem('@LiveChat:token');
    api
      .get('/AtendimentoUser', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data: dataClients } = response.data;
        SetClientsAccepted(dataClients);
      });
  }, []);

  const loadDataClient = useCallback((seqAtendimento: number): void => {
    const token = localStorage.getItem('@LiveChat:token');
    api
      .get(`/AtendimentoUser/${seqAtendimento}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data: dataClient } = response.data;

        SetClientsAccepted((clientes) => [
          ...clientes.filter(
            (cliente) => cliente.seqAtendimento !== seqAtendimento
          ),
          dataClient,
        ]);
      });
  }, []);

  const loadDataClienteRequestService = useCallback(() => {
    const token = localStorage.getItem('@LiveChat:token');
    api
      .get('/MensagemCliente', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data: dataClientService } = response.data;

        SetClientMessageService(dataClientService);
      });
  }, []);

  const loadClientsWaiting = useCallback((): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('/SolicitacaoAtendimento', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data: dataClients } = response.data;
        SetClientWaiting(dataClients);
      });
  }, []);

  const loadChatEmAndamento = useCallback((): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('/ChatSupervisor/ChatEmAndamento', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { data: dataClients } = response.data;
        SetChatEmAndamento(dataClients);
      });
  }, []);

  const getMensages = useCallback((id: number, message: Message[]): void => {
    if (message.length > 0) {
      const newMessages: Messages = {
        messages: message,
        seqAtendimento: id,
      };

      SetMessages((msg) => [
        ...msg.filter((m) => m.seqAtendimento !== id),
        newMessages,
      ]);

      return;
    }

    const token = localStorage.getItem('@LiveChat:token');

    api
      .get(`api/AtendimentoMensagem/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const { success, data } = response.data;

        if (success) {
          const newMessages: Messages = {
            messages: data,
            seqAtendimento: id,
          };

          SetMessages((msg) => [
            ...msg.filter((m) => m.seqAtendimento !== id),
            newMessages,
          ]);
        }
      });
  }, []);

  const getUserGroupMsg: any = useCallback(
    async (idChat: number): Promise<UsersGroupChat> => {
      const usersChat = usersGroupChat.find(
        (chat) => chat.seqGrupoConversa === idChat
      );

      if (usersChat !== undefined) return usersChat;

      const token = localStorage.getItem('@LiveChat:token');
      let chatUsers: UsersGroupChat = {} as UsersGroupChat;

      await api
        .get(`chatUser/usuarios/${idChat}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          if (response.data.success) {
            const usuarios: string[] = [];
            const dataUser = response.data.data;
            dataUser.forEach((usr: any) => {
              usuarios.push(usr.user);
            });

            chatUsers = {
              seqGrupoConversa: idChat,
              usuarios,
            };

            setUsersGroupChat((c) => [
              ...c.filter((x) => x.seqGrupoConversa !== idChat),
              chatUsers,
            ]);
          }

          return chatUsers;
        })
        .catch((err) => {
          console.log(err);
        });
      return chatUsers;
    },
    []
  );

  const getMensagesGroup = useCallback(
    (
      seqGrupoConversa: number,
      message: MessageGroup[],
      chatHistorico: string
    ): void => {
      if (message.length > 0) {
        const newMessages: MessagesGroup = {
          messages: message,
          seqGrupoConversa,
          qtdeMensagensNaoLidas: 1,
        };

        SetMessagesGroup((msg) => [
          ...msg.filter((m) => m.seqGrupoConversa !== seqGrupoConversa),
          newMessages,
        ]);

        return;
      }

      if (chatHistorico === 'S') {
        const chatHistoricoLoaded = messagesGroup.findIndex(
          (chat) => chat.seqGrupoConversa === seqGrupoConversa
        );

        if (chatHistoricoLoaded >= 0) return;
      }

      const token = localStorage.getItem('@LiveChat:token');

      api
        .get(`api/MensagemGrupo/${seqGrupoConversa}/${chatHistorico}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const { success, data } = response.data;

          if (success) {
            const newMessages: MessagesGroup = {
              messages: data,
              seqGrupoConversa,
              qtdeMensagensNaoLidas: data.filter(
                (item: MessageGroup) => item.mensagemLida === 'N'
              ).length,
            };

            SetMessagesGroup((msg) => [
              ...msg.filter((m) => m.seqGrupoConversa !== seqGrupoConversa),
              newMessages,
            ]);
          }
        });
    },
    []
  );

  const addChatGroup = useCallback((data: DataChatUser): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .post('/ChatUser', data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const resultInsertChat = response.data.data;
        SetChatUser((chat) => [
          ...chat.filter(
            (c) => c.seqGrupoConversa !== resultInsertChat.seqGrupoConversa
          ),
          resultInsertChat,
        ]);

        navigate(`/chatUser?id=${resultInsertChat.seqGrupoConversa}`);
      });
  }, []);

  const getChatGroup = useCallback((seqConversa: number): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get(`/ChatUser/${seqConversa}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const resultChat = response.data.data;
        SetChatUser((chat) => [
          ...chat.filter(
            (c) => c.seqGrupoConversa !== resultChat.seqGrupoConversa
          ),
          resultChat,
        ]);
      });
  }, []);

  const getChatGroupMsgPendente = useCallback((): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('/ChatUser/chatPendente', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const resultChatMsgPendente = response.data.data;
        resultChatMsgPendente.forEach((dadosChat: any) => {
          const grupoChat: DataChatUser = {
            seqGrupoConversa: dadosChat.seqGrupoConversa,
            seqGrupo: dadosChat.seqGrupo,
            nomeGrupo: dadosChat.nomeGrupo,
            user: '',
            seqUsuario: 0,
            dataConversa: dadosChat.dataConversa,
            cor: dadosChat.cor,
            qtdeMensagemNaoLida: dadosChat.qtdeMensagemNaoLida,
            chatHistorico: dadosChat.chatHistorico,
          };

          SetChatUser((chat) => [
            ...chat.filter(
              (c) => c.seqGrupoConversa !== dadosChat.seqGrupoConversa
            ),
            grupoChat,
          ]);
        });
      });
  }, []);

  const updateMensageRead = useCallback((seqGrupoConversa: number): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .patch(
        `api/MensagemGrupo/${seqGrupoConversa}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(() => {
        console.log('Mensagem atualziada');
      });
  }, []);

  const updateMensageReadAtendimento = useCallback(
    (seqAtendimento: number): void => {
      const token = localStorage.getItem('@LiveChat:token');

      api
        .patch(
          `api/AtendimentoMensagem/${seqAtendimento}`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then(() => {
          console.log('Mensagem atualziada');
        });
    },
    []
  );

  useEffect(() => {
    if (clientsAccepted === undefined) {
      return;
    }

    clientsAccepted.forEach((client) => {
      getMensages(client.seqAtendimento, []);
    });
  }, [clientsAccepted]);

  const removeMensage = useCallback(
    (
      seqAtendimento: number,
      seqMensagem: number,
      mensagensAtendimento: Messages[],
      mensagensGrupo: MessagesGroup[],
      chatGroup: boolean
    ): void => {
      const token = localStorage.getItem('@LiveChat:token');

      if (chatGroup) {
        api
          .delete(`api/MensagemGrupo/${seqMensagem}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((response) => {
            const { success } = response.data;

            if (success) {
              const msgSeqAtendiemnto = mensagensGrupo.find(
                (mensagemGrupoConversa) =>
                  mensagemGrupoConversa.seqGrupoConversa === seqAtendimento
              );

              if (msgSeqAtendiemnto === undefined) return;

              const newMessages: MessagesGroup = {
                messages: msgSeqAtendiemnto.messages.filter(
                  (msg) => msg.seqGrupoConversaMensagem !== seqMensagem
                ),
                seqGrupoConversa: seqAtendimento,
                qtdeMensagensNaoLidas: msgSeqAtendiemnto.messages.filter(
                  (msg) =>
                    msg.seqGrupoConversaMensagem !== seqMensagem &&
                    msg.mensagemLida === 'N'
                ).length,
              };

              SetMessagesGroup((msg) => [
                ...msg.filter((m) => m.seqGrupoConversa !== seqAtendimento),
                newMessages,
              ]);
            }
          });
      } else {
        api
          .delete(`api/AtendimentoMensagem/${seqMensagem}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((response) => {
            const { success } = response.data;

            if (success) {
              const msgSeqAtendiemnto = mensagensAtendimento.find(
                (mensagemAtendimento) =>
                  mensagemAtendimento.seqAtendimento === seqAtendimento
              );

              if (msgSeqAtendiemnto === undefined) return;

              const newMessages: Messages = {
                messages: msgSeqAtendiemnto.messages.filter(
                  (msg) => msg.seqMensagem !== seqMensagem
                ),
                seqAtendimento,
              };

              SetMessages((msg) => [
                ...msg.filter((m) => m.seqAtendimento !== seqAtendimento),
                newMessages,
              ]);
            }
          });
      }
    },
    []
  );

  function escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function removeTagsHtml(html: string): string {
    if (!html) return '';

    try {
      // Usando uma regex mais simples para remover tags
      return html
        .replace(/<[^>]*>/g, '') // Remove tags
        .replace(/(&nbsp;|&#160;|\u00A0)/gi, ' ') // Substitui todas as formas de espaço não quebrável
        .replace(/\s{2,}/g, ' ') // Remove espaços múltiplos
        .trim();
    } catch (error) {
      console.error('Erro ao remover tags HTML:', error);
      return html;
    }
  }

  const valueContext = useMemo(
    () => ({
      messages,
      messagesGroup,
      clientsAccepted,
      clientWaiting,
      chatEmAndamento,
      chatUser,
      usersGroupChat,
      loadClientsWaiting,
      loadChatEmAndamento,
      loadClientsAccepted,
      SetClientsAccepted,
      SetChatUser,
      getMensages,
      getMensagesGroup,
      removeMensage,
      playSoundNotification,
      playSoundNotificationNewClient,
      loadDataClient,
      editMessage,
      setEditMessage,
      citarMessage,
      setCitarMessage,
      loadDataClienteRequestService,
      SetClientMessageService,
      clientMessageService,
      addChatGroup,
      getChatGroup,
      getChatGroupMsgPendente,
      updateMensageRead,
      updateMensageReadAtendimento,
      getUserGroupMsg,
      removeTagsHtml,
      messageChatCache,
      setMessageChatCache,
      alertNewClientIdTimeout,
      setAlertNewClientIdTimeout,
    }),
    [
      messages,
      messagesGroup,
      clientsAccepted,
      clientWaiting,
      chatEmAndamento,
      chatUser,
      usersGroupChat,
      loadClientsWaiting,
      loadChatEmAndamento,
      loadClientsAccepted,
      SetClientsAccepted,
      SetChatUser,
      getMensages,
      getMensagesGroup,
      removeMensage,
      playSoundNotification,
      playSoundNotificationNewClient,
      loadDataClient,
      editMessage,
      setEditMessage,
      citarMessage,
      setCitarMessage,
      loadDataClienteRequestService,
      SetClientMessageService,
      clientMessageService,
      addChatGroup,
      getChatGroup,
      getChatGroupMsgPendente,
      updateMensageRead,
      updateMensageReadAtendimento,
      getUserGroupMsg,
      removeTagsHtml,
      messageChatCache,
      setMessageChatCache,
      alertNewClientIdTimeout,
      setAlertNewClientIdTimeout,
    ]
  );

  return (
    <MessageContext.Provider value={valueContext}>
      {children}
    </MessageContext.Provider>
  );
};

export { MessageProvider, useMessage };
